import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Eager load critical pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";

// Lazy load non-critical pages
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Compare = lazy(() => import("./pages/Compare"));
const Orders = lazy(() => import("./pages/Orders"));
const Scan = lazy(() => import("./pages/Scan"));
const Install = lazy(() => import("./pages/Install"));
const DriverDashboard = lazy(() => import("./pages/DriverDashboard"));
const DriverOnboardingPage = lazy(() => import("./pages/DriverOnboardingPage"));
const DriverMovingJobs = lazy(() => import("./pages/DriverMovingJobs"));
const VendorOnboarding = lazy(() => import("./pages/VendorOnboarding"));
const MovingJunkBooking = lazy(() => import("./pages/MovingJunkBooking"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DashboardLayout = lazy(() => import("./pages/dashboard/DashboardLayout"));
const DashboardProducts = lazy(() => import("./pages/dashboard/Products"));
const DashboardOrders = lazy(() => import("./pages/dashboard/Orders"));
const DashboardAnalytics = lazy(() => import("./pages/dashboard/Analytics"));
const DashboardStore = lazy(() => import("./pages/dashboard/Store"));
const InventoryConnect = lazy(() => import("./pages/dashboard/InventoryConnect"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const CreateAdminUser = lazy(() => import("./pages/admin/CreateAdminUser"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/" element={<Index />} />
              <Route path="/products" element={<Products />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/driver" element={<ProtectedRoute><DriverDashboard /></ProtectedRoute>} />
              <Route path="/driver-onboarding" element={<ProtectedRoute><DriverOnboardingPage /></ProtectedRoute>} />
              <Route path="/vendor-onboarding" element={<ProtectedRoute><VendorOnboarding /></ProtectedRoute>} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/scan" element={<Scan />} />
              <Route path="/install" element={<Install />} />
              <Route path="/moving-junk" element={<ProtectedRoute><MovingJunkBooking /></ProtectedRoute>} />
              <Route path="/driver/moving-jobs" element={<ProtectedRoute><DriverMovingJobs /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardAnalytics />} />
                <Route path="analytics" element={<DashboardAnalytics />} />
                <Route path="orders" element={<DashboardOrders />} />
                <Route path="products" element={<DashboardProducts />} />
                <Route path="inventory-connect" element={<InventoryConnect />} />
                <Route path="store" element={<DashboardStore />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
